import type { FC } from 'react';
import type React from 'react';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath } from 'react-router-dom';
import { Button, TextField, Typography } from '@mui/material';

import { SECTION } from 'src/constants/section';
import type { DetailedFollowUpTicket } from 'src/types/followUp';
import { apiPaths } from 'src/services/api/apiPaths';
import HttpService from 'src/services/api/http';
import { useUserContext } from 'src/components/app/context/UserContext';
import useUserPermissions from 'src/components/app/context/UserContext/useUserPermissions';
import SuccessSnackbar from 'src/components/common/Snackbar/SuccessSnackbar';
import useTicketDetailQuery from 'src/components/followUp/hooks/useTicketDetailQuery';

import ModalSection from '../ModalSection';

import { styles } from '../ticketDetail.styles';

interface CommentSectionProps {
  ticket: DetailedFollowUpTicket;
  onCommentSave?: () => void;
}

const CommentSection: FC<CommentSectionProps> = ({ ticket, onCommentSave }) => {
  const { t } = useTranslation('followUp');
  const {
    hasReadOnlyPermissionForModuleType,
    hasAdminPermissionForModuleType,
  } = useUserPermissions();
  const { user } = useUserContext();
  const isFollowUpOwner = user && ticket.followUpOwner === user.id;

  const [comment, setComment] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [commentSuccessful, setCommentSuccessful] = useState<boolean>(false);
  const { refetch } = useTicketDetailQuery(ticket.id);

  const saveComment = useCallback(async () => {
    setLoading(true);
    const response = await HttpService.post(
      generatePath(apiPaths.FOLLOW_UP.TICKETS.ADD_COMMENT, {
        id: ticket.id,
      }),
      { contents: comment },
    );
    setLoading(false);

    if (response.status === 201) {
      setComment('');
      setCommentSuccessful(true);
      await refetch();
      onCommentSave?.();
    }
  }, [comment, setComment, loading, setLoading, onCommentSave]);

  // Hide the component if the user has read permission only
  if (
    hasReadOnlyPermissionForModuleType(SECTION.FOLLOW_UP, ticket.surveyId) &&
    !isFollowUpOwner &&
    !hasAdminPermissionForModuleType(SECTION.FOLLOW_UP, ticket.surveyId)
  )
    return null;

  return (
    <ModalSection>
      <Typography>{t('comments.addComment')}</Typography>

      <form style={{ display: 'contents' }}>
        <TextField
          multiline
          rows={3}
          sx={styles.formTextarea}
          value={comment}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setComment(event.target.value);
          }}
          disabled={loading || ticket.status === 'Closed'}
        />

        <Button
          sx={{ alignSelf: 'end', width: 'max-content' }}
          disabled={!comment.length || loading || ticket.status === 'Closed'}
          onClick={saveComment}
        >
          {t('comments.comment')}
        </Button>
      </form>

      <SuccessSnackbar
        open={commentSuccessful}
        onClose={() => setCommentSuccessful(false)}
        title={t('comments.changesSaved')}
        message={t('comments.informationSaved')}
      />
    </ModalSection>
  );
};

export default CommentSection;
